import React from "react";
import { Table } from "react-bootstrap";
import moment from "moment";
import Highlighter from "react-highlight-words";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import "./ChemHistory.css";
import { s3AutomationPrivate } from "../../../../../utils/aws";
import { downloadFileFromS3, getFileFromS3, convertUnit8ArrayToJson } from "../../../../../utils/helpers";
import { parentFolder } from "./ChemHistoryHelper";
import Loading from "../../../../../assets/images/loading.gif";
import { ReactComponent as DownloadIcon } from "../../../../../assets/images/overhaul/icons/DownloadIcon.svg";

class ChemHistory extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      files: [],
      filteredFiles: [],
      historyDate: moment(new Date()).format("MM/DD/YYYY"),
      searchValue: "",
      searchInputValue: "",
      order: "DSC",
      loading: false,
    };
  }

  componentDidMount = () => {
    this.getFiles();
  };

  getFiles = async () => {
    this.setState({ loading: true });
    const { historyDate, order } = this.state;
    const [currentMonth, currentDay, currentYear] = historyDate.split("/");
    const path = `${parentFolder}json/${currentYear}/${currentMonth}.json`;
    const response = await getFileFromS3(path, s3AutomationPrivate);
    let files = [];
    if (response.success) {
      const monthData = convertUnit8ArrayToJson(response.data);
      const dayDataArr = monthData[currentDay] ?? [];
      files = dayDataArr.map((uploadObj) => {
        const uploadMoment = moment(
          uploadObj.uploaded_at,
          [moment.ISO_8601, "YYYY-MM-DD_HHmmss"],
          true,
        );
        return {
          name: uploadObj.excel_file_path?.trim().split("/").filter(Boolean).pop() ?? "",
          api_name: uploadObj.api?.api_name ?? "",
          status_code: uploadObj.api?.status_code ?? "",
          api_message: uploadObj.api?.message ?? "",
          time: uploadMoment.isValid() ? uploadMoment.format("MM/DD/YYYY, h:mm:ss a") : "",
          excel_url: uploadObj.excel_file_path ?? "",
          fap_pdf_url: uploadObj.fap_file_path ?? "",
          selected_test: uploadObj.selected_test ?? "",
          api_response: uploadObj.api?.response ?? "",
        };
      });
    } else if (response.presignedFailed) {
      toast.error("Failed to fetch the presigned URL");
    } else if (response.errorCode && response.errorCode !== "NoSuchKey") {
      toast.error("Failed to fetch the S3 file");
    }
    const sortedFiles = this.sortFiles(order, files);
    this.setState({
      files: sortedFiles,
      loading: false,
    });
  }

  downloadFile = async (file) => {
    const { success, presignedFailed } = await downloadFileFromS3(file.excel_url, s3AutomationPrivate, file.name);

    if (presignedFailed) {
      toast.error("Failed to fetch the presigned URL");
    } else if (!success) {
      toast.error("Failed to fetch the S3 file");
    }
  }

  handleHistoryDate = (e) => {
    let date = e === null ? "" : moment(e.$d).format("MM/DD/YYYY");
    if (date === "Invalid date") {
      date = "";
    }
    this.setState({
      historyDate: date,
      searchValue: "",
      searchInputValue: "",
    }, () => this.getFiles());
  }

  handleSearchSubmit = (e) => {
    if (e.key === "Enter") {
      this.searchFilesByName(e.target.value);
    }
  }

  searchFilesByName = (value) => {
    const { files, order } = this.state;
    const filteredFiles = files.filter((file) => (file.name.toLowerCase().includes(value.toLowerCase()) || file.selected_test.toLowerCase().includes(value.toLowerCase())));

    const sortedFilteredFiles = this.sortFiles(order, filteredFiles);
    this.setState({
      searchValue: value,
      filteredFiles: sortedFilteredFiles,
    });
  }

  handleSearch = (e) => {
    this.setState({
      searchInputValue: e.target.value,
    });
    if (e.target.value === "") {
      this.setState({
        searchValue: e.target.value,
        filteredFiles: [],
      }, () => this.getFiles());
    }
  }

  handleTimeSort = () => {
    const {
      searchValue,
      files,
      filteredFiles,
      order,
    } = this.state;
    const newOrder = order === "DSC" ? "ASC" : "DSC";
    const useDefaultFiles = searchValue === "";
    const currentFiles = useDefaultFiles ? files : filteredFiles;

    const sortedFiles = this.sortFiles(newOrder, currentFiles);

    if (useDefaultFiles) {
      this.setState({
        order: newOrder,
        files: sortedFiles,
      });
    } else {
      this.setState({
        order: newOrder,
        filteredFiles: sortedFiles,
      });
    }
  }

  sortFiles = (order, currentFiles) => {
    if (currentFiles.length === 0) {
      return [];
    }
    const sortedFiles = currentFiles.sort((file1, file2) => {
      const file1Timestamp = Date.parse(file1.time);
      const file2Timestamp = Date.parse(file2.time);

      if (order === "ASC") {
        return file1Timestamp - file2Timestamp;
      }
      return file2Timestamp - file1Timestamp;
    });
    return sortedFiles;
  }

  render() {
    const {
      files,
      filteredFiles,
      historyDate,
      searchValue,
      searchInputValue,
      order,
      loading,
    } = this.state;

    const filesToShow = searchValue ? filteredFiles : files;
    const columns = [
      { field: "name", name: "File" },
      { field: "selected_test", name: "Test" },
      { field: "time", name: "Time" },
      // {field: "api_name", name: "API Name"},
    ];

    return (
      <div className="ChemHistory">
        <div className="ChemHistory__Header">
          <input
            onChange={(e) => this.handleSearch(e)}
            onKeyDown={(e) => this.handleSearchSubmit(e)}
            className="ChemHistory__Header__Search"
            type="text"
            name=""
            id=""
            placeholder="Search by File Name, Test"
            value={searchInputValue}
          />
          <DatePicker
            showNow
            format="MM/DD/YYYY"
            allowClear={false}
            defaultValue={(historyDate === "" || historyDate === "Invalid date") ? "" : dayjs(historyDate)}
            className="ChemHistory__Header__Date"
            disabledDate={(current) => current.isAfter(dayjs())}
            onChange={(e) => this.handleHistoryDate(e)}
          />
        </div>
        {loading && (
          <div className="reportsReadyLoading">
            <img src={Loading} alt="" />
          </div>
        )}
        <Table bordered hover>
          <thead>
            <tr className="ChemHistory__TableHeading">
              {columns.map(({ field, name }) => {
                if (field === "time") {
                  return (
                    <td
                      key={field}
                      className="ChemHistory__TableHeading__Time"
                      onClick={() => this.handleTimeSort()}
                    >
                      {name}
                      <i
                        className={
                          order === "DSC"
                            ? "fa fa-sort-desc sendReportsSortIcon"
                            : "fa fa-sort-asc sendReportsSortIcon"
                        }
                        aria-hidden="true"
                      />
                    </td>
                  );
                }
                return (<td key={field}>{name}</td>);
              })}
            </tr>
          </thead>
          <tbody className="ChemHistory__Tablebody">
            {filesToShow.map((file, index) => (
              <tr key={index}>
                {columns.map(({ field }, i) => {
                  if (field === "name") {
                    return (
                      <td
                        key={i}
                        className="ChemHistory__FileAndIconCell"
                        onClick={() => this.downloadFile(file)}
                      >
                        <div className="ChemHistory__FileAndIcon">
                          <Highlighter
                            highlightClassName="ChemHistory__SearchHighlight"
                            autoEscape
                            searchWords={[searchValue]}
                            textToHighlight={file[field]}
                          />
                          <DownloadIcon className="ChemHistory__DownloadIcon" />
                        </div>
                      </td>
                    );
                  }
                  if (field === "selected_test") {
                    return (
                      <td key={i}>
                        <Highlighter
                          highlightClassName="ChemHistory__SearchHighlight"
                          autoEscape
                          searchWords={[searchValue]}
                          textToHighlight={file[field]}
                        />
                      </td>
                    );
                  }
                  return (<td key={i}>{file[field]}</td>);
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }
}

export default ChemHistory;
